import Service from '@ember/service';
import { computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { EventType, EventsType, Events } from 'cio/utils/analytics/events';
import { timeout } from 'ember-concurrency';
import { restartableTask } from 'ember-concurrency';

import AuthService from 'cio/services/auth';
import ApiService from 'cio/services/api';
import AccountService from 'cio/services/account';
import EnvironmentService from 'cio/services/environment';
import Model from '@ember-data/model';
import Ember from 'ember';
import { resolve, Promise as RSVPPromise } from 'rsvp';
import { UTM_PARAMS, UTM_PARAMS_NAMES } from 'cio/utils/analytics/utm';
import { PASS_THROUGH_QUERY_PARAMS } from 'cio/utils/analytics/generate-transition-event';
import MasqService from './masq';
import { getAnalyticsMetaProperties } from 'cio/utils/get-analytics-meta-properties';
import RouterService from '@ember/routing/router-service';
import RouteInfoWithAttributes from '@ember/routing/-private/route-info-with-attributes';
import RouteInfo from '@ember/routing/-private/route-info';

export const DEFAULT_TIMEOUT = 3000;
export const ANON_ID_KEY = 'cio:anonymous-id';

export type EventProperties = {
  [key: string]: string | number | boolean | Model | Record<string, any> | RouteInfoWithAttributes | undefined | null;
  route?: RouteInfo | RouteInfoWithAttributes;
  passThroughProperties?: Record<string, string>;
};

export default class AnalyticsService extends Service {
  @service account!: AccountService;
  @service environment!: EnvironmentService;
  @service masq!: MasqService;
  @service auth!: AuthService;
  @service api!: ApiService;
  @service router!: RouterService;

  isTesting = Ember.testing;
  canTrack = true;

  get isEnabled() {
    // Disable mixpanel events when masqing, the backend doesn't accept the
    // events anyway
    // https://github.com/customerio/services/blob/18cd9d3b9d37db361c2d2afd0a5a48b9712caefb/ui_api/serve/ctx.go#L381
    return this.canTrack && !this.isTesting && !this.masq.isOn;
  }

  track<T extends keyof EventsType>(eventCategory: T, eventName: keyof EventsType[T], data: EventProperties = {}) {
    let category = Events[eventCategory];
    if (!category) {
      return;
    }

    let event = category[eventName];
    if (!event) {
      return;
    }

    if (!data.route) {
      data.route = this.router.currentRoute;
    }

    // TODO: type conversion was added during TS conversion, shouldn't be needed - but code works
    let eventTyped = event as unknown as EventType;
    return this.trackEvent.perform(eventTyped, data);
  }

  trackTransition(event: EventType, data: EventProperties) {
    if (data.route?.queryParams) {
      let { queryParams } = data.route;
      let passThroughProperties: Record<string, string> =
        data.passThroughProperties != null ? { ...data.passThroughProperties } : {};

      UTM_PARAMS.forEach((param) => {
        if (queryParams[param]) {
          passThroughProperties[UTM_PARAMS_NAMES[param]] = queryParams[param] as string;
        }
      });

      if (PASS_THROUGH_QUERY_PARAMS[data.route.name]) {
        PASS_THROUGH_QUERY_PARAMS[data.route.name].forEach((param) => {
          if (queryParams[param]) {
            data[param] = queryParams[param] as string;
          }
        });
      }

      data.passThroughProperties = passThroughProperties;
    }

    return this.trackEvent.perform(event, data);
  }

  async alias(userId: number): Promise<void> {
    if (!this.isEnabled) {
      return resolve();
    }

    let anonId = this.getAnonId();

    await this.api.post('alias', {
      data: { anon_id: anonId, user_id: String(userId) },
      pathScope: 'global',
    });

    this.discardAnonId();
  }

  getAnonId(): string | null {
    return localStorage.getItem(ANON_ID_KEY);
  }

  private discardAnonId() {
    return localStorage.removeItem(ANON_ID_KEY);
  }

  getOrSetAnonId(): string {
    let id = this.getAnonId();
    if (id) {
      return id;
    }

    let uuid = window.UUID.generate();
    localStorage.setItem(ANON_ID_KEY, uuid);
    return uuid;
  }

  trackEvent = restartableTask(async (event: EventType, data: EventProperties = {}) => {
    // Wraps the trackEventFunc in a Promise to be able to catch (and currently
    // ignore) errors, so tracking errors don't bubble up to the UI, the console,
    // and/or break the app.
    // Using RSVP Promise instead of regular Promises due to issues with tests
    // (particularly with the `settled()` helper)
    return new RSVPPromise<void>((resolve) => {
      this.trackEventFunc(event, data)
        .then(resolve)
        .catch(() => {
          // Ignore and move on for now. The API calls already have ignoreErrors,
          // though that currently doesn't prevent the promise from rejecting,
          // and changing that is potentially risky, as it's used everywhere.
          resolve();
        });
    });
  });

  // separated from trackEvent task to allow overriding in the DemoAnalyticsService:
  async trackEventFunc(event: EventType, data: EventProperties = {}) {
    if (!this.isEnabled) {
      return resolve();
    }

    let name = typeof event.name === 'function' ? event.name(data) : event.name;
    let properties = typeof event.transform === 'function' ? event.transform(data) : {};

    if (data.passThroughProperties) {
      properties = { ...data.passThroughProperties, ...properties };
    }

    // Add some extra context to the events, including a user's Google Client
    // ID, segment ID, etc. -- useful for marketing to identify user sessions.
    properties = {
      ...properties,
      ...getAnalyticsMetaProperties(),
    };

    if (event.debounce?.(data)) {
      await timeout(DEFAULT_TIMEOUT);
    }

    if (this.auth.user) {
      // There's an environment specific track endpoint and there's a non-environment track
      // endpoint (for events sent from the settings section), so we can safely disable environment
      // prefix based on whether the user is within the settings route.
      return this.api.post('track_event', {
        data: { event: name, properties },
        pathScope: this.disableEnvironmentPrefixForCurrentRoute ? 'account' : 'environment',
        ignoreErrors: true,
      });
    } else {
      let anonId = this.getOrSetAnonId();
      return this.api.post('track_anon', {
        data: { anon_id: anonId, event: name, properties },
        pathScope: 'global',
        ignoreErrors: true,
      });
    }
  }

  @computed('router.currentRouteName')
  get disableEnvironmentPrefixForCurrentRoute(): boolean {
    let routes = [
      'settings',
      'authenticated.post_registration',
      'authenticated.account_canceled',
      'post_cancel',
      'trial-expired',
      'no_accessible_workspaces',
      'product-activation',
      'har-sanitizer',
    ];
    return routes.some((route) => this.router.isActive(route));
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    analytics: AnalyticsService;
  }
}
